/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import { useAppSelector } from 'hooks/redux'
import { useGetTermsStatusQuery, useAcceptTermsMutation } from 'store/services/externalApi'
import Toast from 'components/atoms/Toast'
import DashboardContent from '../atoms/DashboardContent'
import routes from '../constants/routes'
import TermsContent from './TermsContent'

const EXTERNAL_API_TERMS_VERSION = '1.0' // Must match backend constant

const AcceptTermsPage = () => {
  const navigate = useNavigate()
  const { adminOf, company } = useAppSelector((state) => state.init)
  const companyId = company?.id ? Number(company.id) : null
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const { data: termsStatus, isLoading } = useGetTermsStatusQuery(undefined, {
    skip: !companyId || adminOf !== 'COMPANY',
  })

  const [acceptTerms, { isLoading: isAccepting }] = useAcceptTermsMutation()

  // Check if user is company admin
  if (adminOf !== 'COMPANY') {
    return (
      <DashboardContent
        title="Access Denied"
        icon="fas fa-lock"
        content={
          <Alert variant="danger">
            <Alert.Heading>Access Restricted</Alert.Heading>
            <p>Only company administrators can accept Terms and Conditions.</p>
          </Alert>
        }
        breadcrumb={[{ label: 'Home', to: `${routes.index}` }, { label: 'External API Terms' }]}
      />
    )
  }

  // Check if terms already accepted
  if (!isLoading && termsStatus && termsStatus.isAccepted) {
    return (
      <DashboardContent
        title="Terms & Conditions"
        icon="fas fa-file-contract"
        content={
          <Alert variant="success">
            <Alert.Heading>
              <i className="fas fa-check-circle mr-2" />
              Terms Already Accepted
            </Alert.Heading>
            <p>
              Terms and Conditions were accepted on{' '}
              {termsStatus.acceptedAt
                ? new Date(termsStatus.acceptedAt).toLocaleDateString()
                : 'an unknown date'}
              .
            </p>
            <Button variant="primary" onClick={() => navigate(`${routes.index}${routes.profile.root}`)}>
              Return to Profile
            </Button>
          </Alert>
        }
        breadcrumb={[{ label: 'Home', to: `${routes.index}` }, { label: 'External API Terms' }]}
      />
    )
  }

  // Check if API not enabled
  if (!isLoading && termsStatus && !termsStatus.requiresAcceptance) {
    return (
      <DashboardContent
        title="Terms & Conditions"
        icon="fas fa-file-contract"
        content={
          <Alert variant="info">
            <Alert.Heading>External API Not Enabled</Alert.Heading>
            <p>External API access is not enabled for your company.</p>
            <Button variant="primary" onClick={() => navigate(`${routes.index}${routes.profile.root}`)}>
              Return to Profile
            </Button>
          </Alert>
        }
        breadcrumb={[{ label: 'Home', to: `${routes.index}` }, { label: 'External API Terms' }]}
      />
    )
  }

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold
        setHasScrolledToBottom(isAtBottom)
      }
    }

    const contentElement = contentRef.current
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll)
      // Check initial state
      handleScroll()
      return () => {
        contentElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const handleAccept = async () => {
    try {
      await acceptTerms({ termsVersion: EXTERNAL_API_TERMS_VERSION }).unwrap()
      Toast({ type: 'success', label: 'Terms and Conditions accepted successfully!' })
      navigate(`${routes.index}${routes.profile.root}`)
    } catch (err: any) {
      Toast({
        type: 'error',
        label: err?.data?.message || 'Failed to accept terms and conditions. Please try again.',
      })
    }
  }

  return (
    <DashboardContent
      title="External API Terms & Conditions"
      icon="fas fa-file-contract"
      content={
        <div className="card card-primary">
          <div className="card-header">
            <h3 className="card-title">Terms & Conditions for External API Access</h3>
          </div>
          <div className="card-body">
            <div
              ref={contentRef}
              style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '20px',
                marginBottom: '20px',
                backgroundColor: '#f8f9fa',
              }}>
              <TermsContent />
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {!hasScrolledToBottom && (
                  <small className="text-muted">
                    Please scroll to the bottom to read all terms before accepting.
                  </small>
                )}
              </div>
              <Button
                variant="warning"
                size="lg"
                onClick={handleAccept}
                disabled={!hasScrolledToBottom || isAccepting}>
                {isAccepting ? 'Accepting...' : 'I Accept the Terms & Conditions'}
              </Button>
            </div>
          </div>
        </div>
      }
      breadcrumb={[
        { label: 'Home', to: `${routes.index}` },
        { label: 'Profile', to: `${routes.index}${routes.profile.root}` },
        { label: 'External API Terms' },
      ]}
    />
  )
}

export default AcceptTermsPage
